import React, { useRef, useState, useMemo } from 'react';
import { marked } from 'marked';
import { FiBold, FiItalic, FiUnderline, FiList } from 'react-icons/fi';
import Icon from '../Icon';

// Configure marked to handle carriage returns and standard markdown safely
marked.setOptions({
    gfm: true,
    breaks: true,
});

type MarkdownProps = {
    content: string;
    className?: string;
};

export function Markdown({ content, className = '' }: MarkdownProps) {
    // Convert content to html safely. Since marked.parse returns a string or promise,
    // we use marked.parseSync or cast to string (marked.parse is sync by default).
    const rawHtml = useMemo(() => {
        try {
            return marked.parse(content || '') as string;
        } catch (e) {
            console.error('Failed to parse markdown', e);
            return content || '';
        }
    }, [content]);

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: `
                .markdown-content strong {
                    font-weight: 700 !important;
                }
                .markdown-content em {
                    font-style: italic !important;
                }
                .markdown-content u {
                    text-decoration: underline !important;
                }
                .markdown-content ul {
                    list-style-type: disc !important;
                    margin-left: 1.5rem !important;
                    margin-top: 0.5rem !important;
                    margin-bottom: 0.5rem !important;
                    padding-left: 0 !important;
                }
                .markdown-content ol {
                    list-style-type: decimal !important;
                    margin-left: 1.5rem !important;
                    margin-top: 0.5rem !important;
                    margin-bottom: 0.5rem !important;
                    padding-left: 0 !important;
                }
                .markdown-content li {
                    display: list-item !important;
                    margin-bottom: 0.25rem !important;
                }
                .markdown-content p {
                    margin-top: 0.5rem !important;
                    margin-bottom: 0.5rem !important;
                }
                .markdown-content p:first-child {
                    margin-top: 0 !important;
                }
                .markdown-content p:last-child {
                    margin-bottom: 0 !important;
                }
            `}} />
            <div
                className={`markdown-content max-w-none text-sm text-gray-800 dark:text-gray-200 ${className}`.trim()}
                dangerouslySetInnerHTML={{ __html: rawHtml }}
            />
        </>
    );
}

type MarkdownEditorProps = {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    rows?: number;
    className?: string;
};

// Helper function to recursively convert HTML DOM elements back to Markdown syntax
function nodeToMarkdown(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
        return node.nodeValue || '';
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        const tagName = element.tagName.toLowerCase();
        
        let childrenMarkdown = '';
        element.childNodes.forEach(child => {
            childrenMarkdown += nodeToMarkdown(child);
        });

        switch (tagName) {
            case 'strong':
            case 'b':
                return `**${childrenMarkdown}**`;
            case 'em':
            case 'i':
                return `*${childrenMarkdown}*`;
            case 'u':
                return `<u>${childrenMarkdown}</u>`;
            case 'ul':
                return `\n${childrenMarkdown}\n`;
            case 'ol': {
                let olMarkdown = '\n';
                let index = 1;
                element.childNodes.forEach(child => {
                    if (child.nodeType === Node.ELEMENT_NODE && (child as HTMLElement).tagName.toLowerCase() === 'li') {
                        const liContent = nodeToMarkdown(child).trim();
                        if (liContent) {
                            olMarkdown += `${index}. ${liContent}\n`;
                            index++;
                        }
                    }
                });
                return olMarkdown + '\n';
            }
            case 'li': {
                const parent = element.parentNode as HTMLElement;
                if (parent && parent.tagName.toLowerCase() === 'ol') {
                    return childrenMarkdown; // Handled by OL parent block
                }
                return `- ${childrenMarkdown.trim()}\n`;
            }
            case 'p':
            case 'div':
                if (childrenMarkdown.trim() === '') {
                    return '';
                }
                return `${childrenMarkdown.trim()}\n`;
            case 'br':
                return '\n';
            default:
                return childrenMarkdown;
        }
    }
    
    return '';
}

function htmlToMarkdown(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return nodeToMarkdown(doc.body).trim();
}

export function MarkdownEditor({
    value,
    onChange,
    placeholder = 'Enter remarks...',
    rows = 3,
    className = '',
}: MarkdownEditorProps) {
    const editorRef = useRef<HTMLDivElement>(null);
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [isList, setIsList] = useState(false);

    // Track the last value we notified the parent about, to prevent resetting innerHTML while typing
    const lastValueRef = useRef(value);

    // Initial load of content on mount
    React.useEffect(() => {
        if (editorRef.current) {
            const html = value ? (marked.parse(value) as string) : '';
            editorRef.current.innerHTML = html;
        }
    }, []);

    // Update from outside if value prop changes independently of typing
    React.useEffect(() => {
        if (value !== lastValueRef.current) {
            lastValueRef.current = value;
            if (editorRef.current) {
                const html = value ? (marked.parse(value) as string) : '';
                if (editorRef.current.innerHTML !== html) {
                    editorRef.current.innerHTML = html;
                }
            }
        }
    }, [value]);

    const handleInput = () => {
        if (!editorRef.current) return;
        const html = editorRef.current.innerHTML;
        const markdown = htmlToMarkdown(html);
        lastValueRef.current = markdown;
        onChange(markdown);
        updateActiveStates();
    };

    const updateActiveStates = () => {
        try {
            setIsBold(document.queryCommandState('bold'));
            setIsItalic(document.queryCommandState('italic'));
            setIsUnderline(document.queryCommandState('underline'));
            setIsList(document.queryCommandState('insertUnorderedList'));
        } catch (e) {
            // Ignore if queryCommandState fails
        }
    };

    const execCmd = (cmd: string, val: string = '') => {
        document.execCommand(cmd, false, val);
        if (editorRef.current) {
            editorRef.current.focus();
        }
        handleInput();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        // Intercept Ctrl+Shift+L for Bullet List
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'L' || e.key === 'l')) {
            e.preventDefault();
            execCmd('insertUnorderedList');
            return;
        }
        // Let browser handle typing and other shortcut keys natively (Ctrl+B/I/U)
        // Just update states on key events
        setTimeout(updateActiveStates, 10);
    };

    const handleMouseUp = () => {
        updateActiveStates();
    };

    return (
        <div className={`wysiwyg-editor-container flex flex-col rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 transition-all ${className}`.trim()}>
            {/* Inline CSS styling for editor */}
            <style dangerouslySetInnerHTML={{ __html: `
                .wysiwyg-editor-container:focus-within {
                    border-color: #3b82f6 !important;
                    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1) !important;
                }
                .wysiwyg-editor {
                    min-height: ${rows * 1.5}rem;
                    outline: none !important;
                }
                .wysiwyg-editor:empty:before {
                    content: attr(data-placeholder);
                    color: #a1a1aa;
                    cursor: text;
                    display: block;
                }
                .wysiwyg-editor strong {
                    font-weight: 700 !important;
                }
                .wysiwyg-editor em {
                    font-style: italic !important;
                }
                .wysiwyg-editor u {
                    text-decoration: underline !important;
                }
                .wysiwyg-editor ul {
                    list-style-type: disc !important;
                    margin-left: 1.5rem !important;
                    margin-top: 0.5rem !important;
                    margin-bottom: 0.5rem !important;
                    padding-left: 0 !important;
                }
                .wysiwyg-editor ol {
                    list-style-type: decimal !important;
                    margin-left: 1.5rem !important;
                    margin-top: 0.5rem !important;
                    margin-bottom: 0.5rem !important;
                    padding-left: 0 !important;
                }
                .wysiwyg-editor li {
                    display: list-item !important;
                    margin-bottom: 0.25rem !important;
                }
            `}} />

            {/* Toolbar */}
            <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/60 px-2 py-1.5 select-none rounded-t-xl">
                <button
                    type="button"
                    onClick={() => execCmd('bold')}
                    className={`group relative p-1.5 rounded-lg transition-colors ${isBold
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                        }`}
                >
                    <Icon icon={FiBold} className="h-3.5 w-3.5" />
                    {/* Custom Tooltip */}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:flex flex-col items-center z-50 pointer-events-none">
                        <div className="bg-gray-900 dark:bg-gray-700 text-white text-[10px] font-medium px-2 py-1 rounded shadow-md flex items-center gap-1 whitespace-nowrap">
                            <span>Bold</span>
                            <span className="text-gray-400 bg-gray-800 dark:bg-gray-600 px-1 rounded text-[9px]">Ctrl+B</span>
                        </div>
                        <div className="w-1.5 h-1.5 bg-gray-900 dark:bg-gray-700 rotate-45 -mt-0.75" />
                    </div>
                </button>
                <button
                    type="button"
                    onClick={() => execCmd('italic')}
                    className={`group relative p-1.5 rounded-lg transition-colors ${isItalic
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                        }`}
                >
                    <Icon icon={FiItalic} className="h-3.5 w-3.5" />
                    {/* Custom Tooltip */}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:flex flex-col items-center z-50 pointer-events-none">
                        <div className="bg-gray-900 dark:bg-gray-700 text-white text-[10px] font-medium px-2 py-1 rounded shadow-md flex items-center gap-1 whitespace-nowrap">
                            <span>Italic</span>
                            <span className="text-gray-400 bg-gray-800 dark:bg-gray-600 px-1 rounded text-[9px]">Ctrl+I</span>
                        </div>
                        <div className="w-1.5 h-1.5 bg-gray-900 dark:bg-gray-700 rotate-45 -mt-0.75" />
                    </div>
                </button>
                <button
                    type="button"
                    onClick={() => execCmd('underline')}
                    className={`group relative p-1.5 rounded-lg transition-colors ${isUnderline
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                        }`}
                >
                    <Icon icon={FiUnderline} className="h-3.5 w-3.5" />
                    {/* Custom Tooltip */}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:flex flex-col items-center z-50 pointer-events-none">
                        <div className="bg-gray-900 dark:bg-gray-700 text-white text-[10px] font-medium px-2 py-1 rounded shadow-md flex items-center gap-1 whitespace-nowrap">
                            <span>Underline</span>
                            <span className="text-gray-400 bg-gray-800 dark:bg-gray-600 px-1 rounded text-[9px]">Ctrl+U</span>
                        </div>
                        <div className="w-1.5 h-1.5 bg-gray-900 dark:bg-gray-700 rotate-45 -mt-0.75" />
                    </div>
                </button>
                <div className="h-4 w-[1px] bg-gray-300 dark:bg-gray-700 mx-1" />
                <button
                    type="button"
                    onClick={() => execCmd('insertUnorderedList')}
                    className={`group relative p-1.5 rounded-lg transition-colors ${isList
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                        }`}
                >
                    <Icon icon={FiList} className="h-3.5 w-3.5" />
                    {/* Custom Tooltip */}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:flex flex-col items-center z-50 pointer-events-none">
                        <div className="bg-gray-900 dark:bg-gray-700 text-white text-[10px] font-medium px-2 py-1 rounded shadow-md flex items-center gap-1 whitespace-nowrap">
                            <span>Bullet List</span>
                            <span className="text-gray-400 bg-gray-800 dark:bg-gray-600 px-1 rounded text-[9px]">Ctrl+Shift+L</span>
                        </div>
                        <div className="w-1.5 h-1.5 bg-gray-900 dark:bg-gray-700 rotate-45 -mt-0.75" />
                    </div>
                </button>
            </div>

            {/* ContentEditable editor element */}
            <div
                ref={editorRef}
                contentEditable
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                onMouseUp={handleMouseUp}
                onKeyUp={handleKeyDown}
                className="wysiwyg-editor w-full p-3 text-sm dark:bg-gray-800 dark:text-gray-100 overflow-y-auto rounded-b-xl"
                data-placeholder={placeholder}
            />
        </div>
    );
}
